import { RecorderConfig, StatusResult, ProcessingResult, RecordingList, ErrorEvent } from '../types';

export class StatusTracker {
  private config: RecorderConfig;
  private pollingIntervals: Map<string, number> = new Map();
  private completionCallbacks: Set<(result: ProcessingResult) => void> = new Set();
  private errorCallbacks: Set<(error: ErrorEvent) => void> = new Set();

  constructor(config: RecorderConfig) {
    this.config = config;
  }

  async getUploadStatus(trackingId: string): Promise<StatusResult> {
    const response = await fetch(`${this.config.apiEndpoint}/status/${trackingId}`, {
      headers: {
        ...(this.config.authHeaders ?? {})
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to get status: ${response.status} ${response.statusText}`);
    }

    return (await response.json()) as StatusResult;
  }

  async listRecordings(userId?: string, page: number = 1): Promise<RecordingList> {
    const params = new URLSearchParams({
      page: String(page),
      ...(userId ? { userId } : {})
    });

    const response = await fetch(`${this.config.apiEndpoint}/recordings?${params}`, {
      headers: {
        ...(this.config.authHeaders ?? {})
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to list recordings: ${response.status} ${response.statusText}`);
    }

    return (await response.json()) as RecordingList;
  }

  startPolling(trackingId: string, intervalMs?: number): void {
    // Use provided interval, config interval, or default to 2000ms
    const pollInterval = intervalMs ?? this.config.pollingIntervalMs ?? 2000;

    if (this.pollingIntervals.has(trackingId)) {
      return; // Already polling
    }

    const poll = async () => {
      try {
        const status = await this.getUploadStatus(trackingId);

        if (status.status === 'completed') {
          this.stopPolling(trackingId);
          this.emitCompletion({
            trackingId,
            status: 'completed',
            hlsUrl: status.urls?.hls,
            mp4Url: status.urls?.mp4,
            webmUrl: status.urls?.webm,
            thumbnails: status.thumbnails,
            // If your API returns duration or other fields, add them here.
          });
        } else if (status.status === 'failed') {
          this.stopPolling(trackingId);
          this.emitError({
            trackingId,
            type: 'processing',
            message: status.error || 'Processing failed',
            retryable: false
          });
        }
      } catch (err) {
        // Keep polling but surface a retryable network error
        console.error('Polling error:', err);
        this.emitError({
          trackingId,
          type: 'network',
          message: 'Failed to check status',
          retryable: true
        });
      }
    };

    const intervalId = window.setInterval(poll, pollInterval);
    this.pollingIntervals.set(trackingId, intervalId);

    // Initial poll immediately
    void poll();
  }

  stopPolling(trackingId: string): void {
    const id = this.pollingIntervals.get(trackingId);
    if (id !== undefined) {
      window.clearInterval(id);
      this.pollingIntervals.delete(trackingId);
    }
  }

  onComplete(callback: (result: ProcessingResult) => void): void {
    this.completionCallbacks.add(callback);
  }

  onError(callback: (error: ErrorEvent) => void): void {
    this.errorCallbacks.add(callback);
  }

  private emitCompletion(result: ProcessingResult): void {
    this.completionCallbacks.forEach((callback) => {
      try {
        callback(result);
      } catch (err) {
        console.error('Error in completion callback:', err);
      }
    });
  }

  private emitError(error: ErrorEvent): void {
    this.errorCallbacks.forEach((callback) => {
      try {
        callback(error);
      } catch (err) {
        console.error('Error in error callback:', err);
      }
    });
  }

  cleanup(): void {
    // Stop all polling
    this.pollingIntervals.forEach((id) => window.clearInterval(id));
    this.pollingIntervals.clear();

    // Clear callbacks
    this.completionCallbacks.clear();
    this.errorCallbacks.clear();
  }
}
